import { useNode } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  MarginInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RangeNumberInput,
  TabsV2,
  PaddingInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";

const JUSTIFYCONTENT = [
  {
    label: "Start",
    value: "start",
  },
  {
    label: "Center",
    value: "center",
  },
  {
    label: "End",
    value: "end",
  },
];

export const PostAuthorSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    typoNameSize,
    typoNameLineHeight,
    typoNameLetterSpacing,
    typoNameWordSpacing,
    typoNameTransform,
    typoNameStyle,
    typoNameDecoration,
    typoNameWeight,
    typoBioSize,
    typoBioLineHeight,
    typoBioLetterSpacing,
    typoBioWordSpacing,
    typoBioTransform,
    typoBioStyle,
    typoBioDecoration,
    typoBioWeight,
    align,
    border,
    layoutPadding,
    layoutMargin,
    borderRadius,
    showAvatar = true,
    showName = true,
    showBio = true,
    authorNameColor,
    authorBioColor,
    backgroundColor,
    structureTag = "h2",
    boxShadow,
    source,
    link,
    openInNewTab,
    layout = "left",
    avatarVerticalAlign = "start",
    avatarVerticalAlignTablet,
    avatarVerticalAlignMobile,
    avatarSize = "96px",
    avatarSizeTablet,
    avatarSizeMobile,
    avatarGap = "10px",
    avatarGapTablet,
    avatarGapMobile,
    avatarBorder,
    avatarBorderRadius,
    hoverAuthorNameColor,
    hoverAuthorBioColor,
    hoverBackgroundColor,
    hoverAvatarBorder,
    hoverAvatarBorderRadius,
    hoverBorder,
    hoverBorderRadius,
    hoverBoxShadow,
    hoverLayoutPadding,
    hoverLayoutMargin,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={"left"}
            value={layout}
            onChange={(val) => {
              setProp((props) => {
                props.layout = val;
              });
            }}
            options={[
              {
                label: "Left",
                value: "left",
              },
              {
                label: "Above",
                value: "above",
              },
              {
                label: "Right",
                value: "right",
              },
            ]}
            label={"Layout"}
          />
          {/* <SelectInput
            isResetVisible={false}
            value={source}
            onChange={(val) => {
              setProp((props) => {
                props.source = val;
              });
            }}
            options={[
              {
                label: "Current Author",
                value: "",
              },
            ]}
            label={"Source"}
          /> */}
          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
          <SelectInput
            resetValue={"h2"}
            value={structureTag}
            onChange={(val) => {
              setProp((props) => {
                props.structureTag = val;
              });
            }}
            options={[
              {
                label: "Paragraph",
                value: "p",
              },
              {
                label: "Heading 1",
                value: "h1",
              },
              {
                label: "Heading 2",
                value: "h2",
              },
              {
                label: "Heading 3",
                value: "h3",
              },
              {
                label: "Heading 4",
                value: "h4",
              },
              {
                label: "Heading 5",
                value: "h5",
              },
              {
                label: "Heading 6",
                value: "h6",
              },
            ]}
            label={"Tag"}
          />

          <ToggleInput
            resetValue={true}
            label="Show avatar"
            value={showAvatar}
            onChange={(val) => {
              setProp((props) => {
                props.showAvatar = val;
              });
            }}
          />
          <ToggleInput
            resetValue={true}
            label="Show name"
            value={showName}
            onChange={(val) => {
              setProp((props) => {
                props.showName = val;
              });
            }}
          />
          <ToggleInput
            resetValue={true}
            label="Show Bio"
            value={showBio}
            onChange={(val) => {
              setProp((props) => {
                props.showBio = val;
              });
            }}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: avatarVerticalAlignTablet },
            { key: avatarVerticalAlignMobile },
            { key: avatarVerticalAlign, default: "start" },
            { key: avatarSize, default: "96px" },
            { key: avatarSizeTablet },
            { key: avatarSizeMobile },
            { key: avatarGap, default: "10px" },
            { key: avatarGapTablet },
            { key: avatarGapMobile },
          ])}
          id={`block-setting-${id}-avatar`}
          title={"Avatar"}
          initialOpen={false}
        >
          <SelectInput
            // isResponsive={true}
            valueTablet={avatarVerticalAlignTablet}
            valueMobile={avatarVerticalAlignMobile}
            resetValue={"start"}
            value={avatarVerticalAlign}
            onChange={(val) => {
              setProp((props) => {
                props.avatarVerticalAlign = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.avatarVerticalAlignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.avatarVerticalAlignMobile = val;
              });
            }}
            options={JUSTIFYCONTENT}
            label="Vertical Align"
          />
          <UnitInput
            // isResponsive={true}
            resetValue={"96px"}
            value={avatarSize}
            valueTablet={avatarSizeTablet}
            valueMobile={avatarSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.avatarSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.avatarSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.avatarSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            // isResponsive={true}
            resetValue={"10px"}
            value={avatarGap}
            valueTablet={avatarGapTablet}
            valueMobile={avatarGapMobile}
            onChange={(val) => {
              setProp((props) => {
                props.avatarGap = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.avatarGapTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.avatarGapMobile = val;
              });
            }}
            label={"Gap"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoNameLetterSpacing },
            { key: typoNameWordSpacing },
            { key: typoNameWeight },
            { key: typoNameTransform },
            { key: typoNameDecoration },
            { key: typoNameStyle },
            { key: typoNameSize },
            { key: typoNameLineHeight },
          ])}
          id={`block-setting-${id}-typography-name`}
          title={"Typography - Name"}
          initialOpen={false}
        >
          <UnitInput
            resetValue={undefined}
            value={typoNameSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameSize = val;
              });
            }}
            label={"Size"}
          />

          <UnitInput
            resetValue={undefined}
            value={typoNameLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoNameLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoNameWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoNameWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoNameTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoNameDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoNameStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoNameStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoBioSize },
            { key: typoBioLineHeight },
            { key: typoBioLetterSpacing },
            { key: typoBioWordSpacing },
            { key: typoBioWeight },
            { key: typoBioTransform },
            { key: typoBioDecoration },
            { key: typoBioStyle },
          ])}
          id={`block-setting-${id}-typography-bio`}
          title={"Typography - Bio"}
          initialOpen={false}
        >
          <UnitInput
            resetValue={undefined}
            value={typoBioSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoBioLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoBioLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoBioWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoBioWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoBioTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioTransform = val;
              });
            }}
            options={["uppercase", "lowercase", "capitalize", "none"].map(
              function (item) {
                return {
                  label: item,
                  value: item,
                };
              },
            )}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoBioDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioDecoration = val;
              });
            }}
            options={["underline", "overline", "none"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoBioStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoBioStyle = val;
              });
            }}
            options={["normal", "italic"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Style"
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: link },
            { key: openInNewTab },
          ])}
          id={`block-setting-${id}-link`}
          title={"Link"}
          initialOpen={false}
        >
          <SelectInput
            isResetVisible={false}
            value={link}
            onChange={(val) => {
              setProp((props) => {
                props.link = val;
              });
            }}
            options={[
              {
                label: "None",
                value: "",
              },
              {
                label: "Author Page",
                value: "author-page",
              },
              {
                label: "CMS Post",
                value: "cms-post",
              },
            ]}
            label={"Link"}
          />
          <ToggleInput
            resetValue={undefined}
            label="Open in new tab"
            value={openInNewTab}
            onChange={(val) => {
              setProp((props) => {
                props.openInNewTab = val;
              });
            }}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          authorNameColorPropKey: "authorNameColor",
          authorNameColorProp: authorNameColor,
          authorBioColorPropKey: "authorBioColor",
          authorBioColorProp: authorBioColor,
          backgroundColorPropKey: "backgroundColor",
          backgroundColorProp: backgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          authorNameColorPropKey: "hoverAuthorNameColor",
          authorNameColorProp: hoverAuthorNameColor,
          authorBioColorPropKey: "hoverAuthorBioColor",
          authorBioColorProp: hoverAuthorBioColor,
          backgroundColorPropKey: "hoverBackgroundColor",
          backgroundColorProp: hoverBackgroundColor,
        }),
      },
    ];

    function handleColorStates({
      authorNameColorPropKey,
      authorNameColorProp,
      authorBioColorPropKey,
      authorBioColorProp,
      backgroundColorPropKey,
      backgroundColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={authorNameColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[authorNameColorPropKey] = val;
              });
            }}
            label={"Name"}
          />{" "}
          <ColorInput
            resetValue={undefined}
            value={authorBioColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[authorBioColorPropKey] = val;
              });
            }}
            label={"Bio"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[backgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(authorBioColorPropKey === "hoverAuthorBioColor" ||
            backgroundColorPropKey === "hoverBackgroundColor" ||
            authorNameColorPropKey === "hoverAuthorNameColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing="1rem">
            <TabsV2 items={colorTabs} />
          </Spacer>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    const containerLayoutTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleLayout({
          layoutMarginPropKey: "layoutMargin",
          layoutMarginProp: layoutMargin,
          layoutPaddingPropKey: "layoutPadding",
          layoutPaddingProp: layoutPadding,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleLayout({
          layoutMarginPropKey: "hoverLayoutMargin",
          layoutMarginProp: hoverLayoutMargin,
          layoutPaddingPropKey: "hoverLayoutPadding",
          layoutPaddingProp: hoverLayoutPadding,
        }),
      },
    ];

    function handleLayout({
      layoutMarginPropKey,
      layoutMarginProp,
      layoutPaddingPropKey,
      layoutPaddingProp,
    }) {
      return (
        <>
          <MarginInput
            resetValue={undefined}
            value={layoutMarginProp}
            onChange={(val) => {
              setProp((props) => {
                props[layoutMarginPropKey] = val;
              });
            }}
            label={"Margin"}
          />

          <PaddingInput
            resetValue={undefined}
            value={layoutPaddingProp}
            onChange={(val) => {
              setProp((props) => {
                props[layoutPaddingPropKey] = val;
              });
            }}
            label={"Padding"}
          />

          {(layoutPaddingPropKey === "hoverLayoutPadding" ||
            layoutMarginPropKey === "hoverLayoutMargin") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.[("margin", "padding")]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["margin", "padding"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    const borderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderStates({
          borderPropKey: "border",
          borderProp: border,
          borderRadiusPropKey: "borderRadius",
          borderRadiusProp: borderRadius,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderStates({
          borderPropKey: "hoverBorder",
          borderProp: hoverBorder,
          borderRadiusPropKey: "hoverBorderRadius",
          borderRadiusProp: hoverBorderRadius,
        }),
      },
    ];

    function handleBorderStates({
      borderPropKey,
      borderProp,
      borderRadiusPropKey,
      borderRadiusProp,
    }) {
      return (
        <>
          <BorderInput
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />
          {(borderRadiusPropKey === "hoverBorderRadius" ||
            borderPropKey === "hoverBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.[("border", "border-radius")]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border", "border-radius"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    const avatarBorderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleAvatarBorderStates({
          avatarBorderPropKey: "avatarBorder",
          avatarBorderProp: avatarBorder,
          avatarBorderRadiusPropKey: "avatarBorderRadius",
          avatarBorderRadiusProp: avatarBorderRadius,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleAvatarBorderStates({
          avatarBorderPropKey: "hoverAvatarBorder",
          avatarBorderProp: hoverAvatarBorder,
          avatarBorderRadiusPropKey: "hoverAvatarBorderRadius",
          avatarBorderRadiusProp: hoverAvatarBorderRadius,
        }),
      },
    ];

    function handleAvatarBorderStates({
      avatarBorderPropKey,
      avatarBorderProp,
      avatarBorderRadiusPropKey,
      avatarBorderRadiusProp,
    }) {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={avatarBorderProp}
            onChange={(val) => {
              setProp((props) => {
                props[avatarBorderPropKey] = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={avatarBorderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[avatarBorderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />
          {(avatarBorderRadiusPropKey === "hoverAvatarBorderRadius" ||
            avatarBorderPropKey === "hoverAvatarBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutPadding },
            { key: hoverLayoutMargin },
            { key: hoverLayoutPadding },
          ])}
          id={`block-setting-${id}-container-layout`}
          title={"Container Layout"}
          initialOpen={false}
        >
          <TabsV2 items={containerLayoutTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
            { key: hoverBorder },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-container-border`}
          title={"Container Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: avatarBorder },
            { key: avatarBorderRadius },
            { key: hoverAvatarBorder },
            { key: hoverAvatarBorderRadius },
          ])}
          id={`block-setting-${id}-avatar-border`}
          title={"Avatar Border"}
          initialOpen={false}
        >
          <TabsV2 items={avatarBorderTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-border`}
          title={"Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layout },
        { key: align },
        { key: structureTag },
        { key: showAvatar },
        { key: showName },
        { key: showBio },
        { key: avatarVerticalAlignTablet },
        { key: avatarVerticalAlignMobile },
        { key: avatarVerticalAlign, default: "start" },
        { key: avatarSize, default: "96px" },
        { key: avatarSizeTablet },
        { key: avatarSizeMobile },
        { key: avatarGap, default: "10px" },
        { key: avatarGapTablet },
        { key: avatarGapMobile },
        { key: typoNameLetterSpacing },
        { key: typoNameWordSpacing },
        { key: typoNameWeight },
        { key: typoNameTransform },
        { key: typoNameDecoration },
        { key: typoNameStyle },
        { key: typoNameSize },
        { key: typoNameLineHeight },
        { key: typoBioSize },
        { key: typoBioLineHeight },
        { key: typoBioLetterSpacing },
        { key: typoBioWordSpacing },
        { key: typoBioWeight },
        { key: typoBioTransform },
        { key: typoBioDecoration },
        { key: typoBioStyle },
        { key: link },
        { key: openInNewTab },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: authorNameColor },
        { key: authorBioColor },
        { key: backgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutPadding },
        { key: border },
        { key: borderRadius },
        { key: avatarBorder },
        { key: avatarBorderRadius },
        { key: boxShadow },
        { key: hoverLayoutMargin },
        { key: hoverLayoutPadding },
        { key: hoverBorder },
        { key: hoverBoxShadow },
        { key: hoverBorderRadius },
        { key: hoverAvatarBorder },
        { key: hoverAvatarBorderRadius },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
